import * as React from 'react'

export type MetaValue = string | number
export type Meta = {[key: string]: MetaValue}
export type Product<M extends Meta = Meta> = {
	id: string
	meta?: M
	randomKey?: string
}

type ProductsPerIdType = Array<ProductsPerIdItemType>
type ProductsPerIdItemType = {
	productId: string
	variants: Array<Product>
}
type CartEntry = {product: Product; quantity: number}

interface CartData {
	readonly products: ReadonlyArray<Product>
	readonly productsPerId: ProductsPerIdType
	quantityOf(variant: Product): number
	quantityOfVariantsWith(productId: string): number
	quantityTotal(): number
	has(variant: Product): boolean
}

interface CartModifiers {
	addProduct(product: Product, quantity: number): Cart
	setMeta<M extends Meta>(product: Product<M>, meta: M): Cart
	removeProduct(product: Product): Cart
	setQuantity(product: Product, quantity: number): Cart
	setQuantityAndMeta<M extends Meta>(
		product: Product,
		quantity: number,
		meta?: M
	): Cart
	clearCart(): Cart
}

export const hashProduct = (product: Product) => {
	return (
		product.id +
		'|' +
		(product.meta
			? JSON.stringify(product.meta, Object.keys(product.meta).sort())
			: '')
	)
}

export class Cart implements CartData, CartModifiers {
	constructor(private readonly cart: ReadonlyMap<string, CartEntry>) {}
	// Storage, these will load or pesist a cart instance
	// to local storage. They're guarded by a catch to allow
	// the cart to properly function when no storage access
	// is allowed, for example in private sessions.

	static fromStorage(storageKey: string) {
		try {
			const values = JSON.parse(localStorage.getItem(storageKey)!)
			const entries = values.map((value: CartEntry) => [
				hashProduct(value.product),
				value
			])
			return new Cart(new Map(entries))
		} catch (e) {
			return new Cart(new Map())
		}
	}

	// Derived, these simply "query" our data but
	// do not modify it

	get products() {
		return Array.from(this.cart.values()).map(({product}) => product)
	}

	get productsPerId() {
		const products = new Map<string, Array<Product>>()
		for (const product of this.products) {
			if (products.has(product.id)) products.get(product.id)!.push(product)
			else products.set(product.id, [product])
		}
		return Array.from(products.entries()).map(([productId, variants]) => {
			return {
				productId,
				variants
			}
		})
	}

	quantityOf(variant: Product): number {
		const key = hashProduct(variant)
		return this.cart.get(key)?.quantity || 0
	}

	quantityOfVariantsWith(productId: string): number {
		let countItemsWithId = 0
		this.cart.forEach((item) => {
			if (item.product.id !== productId) return
			countItemsWithId += item.quantity
		})
		return countItemsWithId
	}

	quantityTotal(): number {
		let countedItems = 0
		this.cart.forEach((item) => {
			countedItems += item.quantity
		})
		return countedItems
	}

	has(product: Product): boolean {
		return this.quantityOf(product) > 0
	}

	all() {
		return Array.from(this.cart.values())
	}

	// Modifiers, these return a new cart instance
	// with modified data

	addProduct(product: Product, quantity: number): Cart {
		const productWithKey: Product = {
			randomKey: this.makeRandomKey(),
			...product
		}
		return this.setQuantity(
			productWithKey,
			this.quantityOf(productWithKey) + quantity
		)
	}

	removeProduct(product: Product): Cart {
		return this.setQuantity(product, 0)
	}

	clearCart(): Cart {
		return new Cart(new Map())
	}

	setQuantity(product: Product, quantity: number) {
		if (this.quantityOf(product) === quantity) return this
		const key = hashProduct(product)
		const res = new Map(this.cart)
		if (quantity > 0) res.set(key, {product, quantity})
		else res.delete(key)
		return new Cart(res)
	}

	setQuantityAndMeta<M extends Meta>(
		product: Product,
		quantity: number,
		meta?: M
	) {
		return this.setQuantity(product, quantity).setMeta(
			product,
			meta || product.meta || {}
		)
	}

	setMeta<M extends Meta>(product: Product, meta: M) {
		if (!this.has(product)) return this
		const quantity = this.quantityOf(product)
		const randomKey = product.randomKey
		const insert = {id: product.id, meta, randomKey}
		const existing = this.quantityOf(insert)
		const oldKey = hashProduct(product)
		const newKey = hashProduct(insert)
		if (oldKey === newKey) return this
		const res = new Map()
		for (const inCart of this.products) {
			const key = hashProduct(inCart)
			if (key === oldKey)
				res.set(newKey, {product: insert, quantity: quantity + existing})
			else if (key === newKey) continue
			else res.set(key, this.cart.get(key))
		}
		return new Cart(res)
	}

	toStorage(storageKey: string) {
		try {
			localStorage.setItem(storageKey, JSON.stringify(this.all()))
		} catch (e) {
			console.error(`Cannot persist cart because`, e)
		}
	}

	makeRandomKey() {
		// https://stackoverflow.com/a/1349426/7883273
		let result = ''
		const characters =
			'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
		const charactersLength = characters.length
		for (let i = 0; i < 8; i++) {
			result += characters.charAt(Math.floor(Math.random() * charactersLength))
		}
		return result
	}
}

type CartApi = {
	cart: CartData
} & CartModifiers

const CartContext = React.createContext<CartApi>(null!)

export const CartProvider: React.FunctionComponent<{storageKey: string}> = ({
	storageKey,
	children
}) => {
	const [cart, setCart] = React.useState(() => Cart.fromStorage(storageKey))

	// If our cart contents were changed in another tab
	// we'll reload our instance from disk so we don't
	// get out of sync
	React.useEffect(() => {
		const listen = (e: StorageEvent) => {
			if (e.key === storageKey) setCart(Cart.fromStorage(storageKey))
		}
		window.addEventListener('storage', listen)
		return () => window.removeEventListener('storage', listen)
	}, [storageKey])

	// We simply wrap all modifier methods of current cart
	// instance below so any modification will result in a
	// new cart instance in our state and persistance to local storage
	const modify = <T extends (...args: any) => Cart>(method: T) => (
		...args: Parameters<T>
	) => {
		const modified = method.apply(cart, args)
		modified.toStorage(storageKey)
		setCart(modified)
		return modified
	}

	const api: CartApi = {
		cart,
		clearCart: modify(cart.clearCart),
		addProduct: modify(cart.addProduct),
		removeProduct: modify(cart.removeProduct),
		setQuantity: modify(cart.setQuantity),
		setQuantityAndMeta: modify(cart.setQuantityAndMeta),
		setMeta: modify(cart.setMeta)
	}

	return <CartContext.Provider value={api}>{children}</CartContext.Provider>
}

export const useCart = () => React.useContext(CartContext)
